import * as THREE from 'three'
import * as d3 from 'd3'

import { FontLoader } from 'three/examples/jsm/loaders/FontLoader'
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry'

import BattersBox from './batters-box'
import HomePlate from './home-plate'
import StrikeZone from './strike-zone'
import Pitch from './pitch'

import {
    getBins,
    x_extent,
    x_step,
    y_extent,
    y_step,
    z_extent,
    z_step,
} from './bin-data'

export default class CatcherVisual {
    constructor({ data, player_id, player_name, year, is_team_type }) {
        this.data = data
        this.player_id = player_id
        this.player_name = player_name
        this.year = year
        if (is_team_type) {
            this.first_last = player_name
        } else {
            const [last_name, first_name] = player_name.split(', ')
            this.first_last = `${first_name} ${last_name}`
        }

        this.filtered_data = this.data
        this.shadows = []

        this.show_pitches = false

        this.binData()

        this.group = new THREE.Group()
        this.group.name = 'catcher-visual'
        this.group.userData.player_id = player_id

        // Context shapes
        this.addContext()

        this.addXYBins()
        this.addXZBins()

        this.bin_objects = [...this.xy_bin_objects, ...this.xz_bin_objects]

        this.createPitchObjects()

        this.addImage()
        this.addText()

        this.addLights()

        this.addGround()
    }

    addImage() {
        const image = new THREE.TextureLoader().load(
            `https://img.mlbstatic.com/mlb-photos/image/upload/w_180,q_100/v1/people/${this.player_id}/headshot/silo/current`
        )
        const geometry = new THREE.PlaneGeometry(1, 1, 1, 1)
        const material = new THREE.MeshBasicMaterial({
            map: image,
        })

        const mesh = new THREE.Mesh(geometry, material)

        mesh.rotateX(-Math.PI / 2)
        mesh.rotateZ(Math.PI)

        mesh.position.set(0, -2.5, 6.5)
        mesh.scale.set(2, 2, 1)

        // this.group.add(mesh)

        const mesh2 = new THREE.Mesh(geometry, material)

        mesh2.rotateZ(Math.PI)
        mesh2.position.set(0, 6, 0)
        mesh2.scale.set(3, 3, 1)

        // this.group.add(mesh2)
    }

    addText() {
        const loader = new FontLoader()
        loader.load(
            'https://threejs.org/examples/fonts/helvetiker_regular.typeface.json',
            (font) => {
                const clean = this.first_last
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                const text_geometry = new TextGeometry(clean, {
                    font,
                    size: 0.6,
                    height: 0.1,
                    curveSegments: 12,
                    bevelEnabled: false,
                })

                text_geometry.computeBoundingBox()
                const center_offset =
                    0.5 *
                    (text_geometry.boundingBox.max.x -
                        text_geometry.boundingBox.min.x)

                const text_material = new THREE.MeshPhongMaterial({
                    color: 0xffffff,
                })
                this.text = new THREE.Group()
                this.text.visible = false

                this.name_text = new THREE.Mesh(text_geometry, text_material)
                this.name_text.position.set(center_offset, 5.5, 0)
                this.name_text.rotateZ(Math.PI)

                this.text.add(this.name_text)

                if (this.year) {
                    const year_text_geometry = new TextGeometry(
                        String(this.year),
                        {
                            font,
                            size: 0.36,
                            height: 0.1,
                            curveSegments: 12,
                            bevelEnabled: false,
                        }
                    )
                    year_text_geometry.computeBoundingBox()
                    const center_offset_year =
                        0.5 *
                        (year_text_geometry.boundingBox.max.x -
                            year_text_geometry.boundingBox.min.x)
                    this.year_text = new THREE.Mesh(
                        year_text_geometry,
                        text_material
                    )
                    this.year_text.position.set(center_offset_year, 6.3, 0)
                    this.year_text.rotateZ(Math.PI)
                    this.text.add(this.year_text)
                }

                this.group.add(this.text)
            }
        )
    }

    addGround() {
        const circle_geometry = new THREE.CircleGeometry(8, 128)
        const circle_material = new THREE.MeshLambertMaterial({
            // color: 0xb6b8ab,
            color: 0x5e3000,
        })
        this.ground = new THREE.Mesh(circle_geometry, circle_material)
        this.ground.position.z = -0.25
        this.group.add(this.ground)
    }

    addContext() {
        // Batters Boxes
        this.batters_box = new BattersBox()
        this.group.add(this.batters_box.group)

        // Home Plate
        this.home_plate = new HomePlate()
        this.group.add(this.home_plate.object)

        // Zone
        this.strike_zone = new StrikeZone()
        this.group.add(this.strike_zone.object)
    }

    // In the dirt
    addXYBins() {
        const thickness = 0.1
        const margin = 0.05
        // const xz_geometry = new THREE.BoxGeometry(
        //     Math.abs(x_step) - 2 * margin,
        //     thickness,
        //     Math.abs(z_step) - 2 * margin
        // );
        const xy_geometry = new THREE.BoxGeometry(
            Math.abs(x_step) - 2 * margin,
            Math.abs(y_step) - 2 * margin,
            thickness
        )
        const xy_geometry_shadow = new THREE.BoxGeometry(
            Math.abs(x_step) + margin,
            Math.abs(y_step) + margin,
            thickness
        )
        this.xy_bins = new THREE.Group()

        this.xy_bin_objects = this.xy_bins_flat_unfiltered.map((bin) => {
            // TODO: How to handle coloring?
            // const color = getColor(bin.bin);
            const color = 0xff0000
            const material = new THREE.MeshBasicMaterial({
                color,
                side: THREE.DoubleSide,
            })
            const material_shadow = new THREE.MeshBasicMaterial({
                color: 0xff6c00,
                side: THREE.DoubleSide,
            })
            material.transparent = true
            const xy_bin = new THREE.Mesh(xy_geometry, material)
            xy_bin.name = 'bin'
            xy_bin.position.x = x_extent[0] + bin.x_idx * x_step + x_step / 2
            xy_bin.position.y = y_extent[0] + bin.y_idx * y_step + y_step / 2
            xy_bin.position.z = -0.25 // just enough to see some daylight between the planes

            const xy_bin_shadow = new THREE.Mesh(
                xy_geometry_shadow,
                material_shadow
            )
            xy_bin_shadow.visible = false
            xy_bin_shadow.name = 'bin_shadow'
            xy_bin_shadow.position.x =
                x_extent[0] + bin.x_idx * x_step + x_step / 2
            xy_bin_shadow.position.y =
                y_extent[0] + bin.y_idx * y_step + y_step / 2
            xy_bin_shadow.position.z = -0.275

            const group = new THREE.Group()
            group.userData = bin
            group.add(xy_bin)
            group.add(xy_bin_shadow)

            this.xy_bins.add(group)
            this.shadows.push(xy_bin_shadow)

            return group
        })

        this.group.add(this.xy_bins)
    }

    // In the air
    addXZBins() {
        const thickness = 0.1
        const margin = 0.05
        const xz_geometry = new THREE.BoxGeometry(
            Math.abs(x_step) - 2 * margin,
            thickness,
            Math.abs(z_step) - 2 * margin
        )
        const xz_geometry_shadow = new THREE.BoxGeometry(
            Math.abs(x_step) + margin,
            thickness,
            Math.abs(z_step) + margin
        )
        this.xz_bins = new THREE.Group()

        this.xz_bin_objects = this.xz_bins_flat_unfiltered.map((bin) => {
            // const color = getColor(bin.bin);
            const color = 0xff00ff
            const material = new THREE.MeshBasicMaterial({
                color,
                side: THREE.DoubleSide,
            })
            material.transparent = true
            const material_shadow = new THREE.MeshBasicMaterial({
                color: 0xff6c00,
                side: THREE.DoubleSide,
            })

            const xz_bin = new THREE.Mesh(xz_geometry, material)
            xz_bin.name = 'bin'
            xz_bin.position.x = x_extent[0] + bin.x_idx * x_step + x_step / 2
            xz_bin.position.y = -2.8
            xz_bin.position.z = z_extent[0] + bin.y_idx * z_step + z_step / 2
            this.xz_bins.add(xz_bin)

            const xz_bin_shadow = new THREE.Mesh(
                xz_geometry_shadow,
                material_shadow
            )
            xz_bin_shadow.visible = false
            xz_bin_shadow.name = 'bin_shadow'
            xz_bin_shadow.position.x =
                x_extent[0] + bin.x_idx * x_step + x_step / 2
            xz_bin_shadow.position.y = -2.85
            xz_bin_shadow.position.z =
                z_extent[0] + bin.y_idx * z_step + z_step / 2

            const group = new THREE.Group()
            group.userData = bin
            group.add(xz_bin)
            group.add(xz_bin_shadow)

            this.xz_bins.add(group)
            this.shadows.push(xz_bin_shadow)

            return group
        })

        this.group.add(this.xz_bins)
    }

    binData() {
        const { xy_bins_flat, xz_bins_flat } = getBins(this.filtered_data)
        this.xy_bins_flat = xy_bins_flat.filter((d) => d.bin.length > 0)
        this.xz_bins_flat = xz_bins_flat.filter((d) => d.bin.length > 0)
        // Do we need these?
        this.xy_bins_flat_unfiltered = xy_bins_flat
        this.xz_bins_flat_unfiltered = xz_bins_flat
    }

    createPitchObjects() {
        // This turns out to be just an estimate based on other offsets...
        const len = this.filtered_data.length
        const duration = len > 10 ? 2 : 0.5

        const freq = len / duration

        this.pitch_group = new THREE.Group()

        this.pitches = d3.shuffle(this.filtered_data).map((data, i) => {
            const pitch = new Pitch(
                data,
                i / freq,
                this.filtered_data.length,
                this.show_pitches
            )
            this.pitch_group.add(pitch.object)
            return pitch
        })

        this.pitch_map = this.pitches.reduce((acc, pitch) => {
            acc[pitch.data.play_id] = pitch
            return acc
        }, {})

        this.group.add(this.pitch_group)
    }

    hidePitches() {
        this.pitches.forEach((pitch) => pitch.hide())
    }

    showPitches(value = 'all') {
        this.pitches.forEach((pitch) => {
            if (value === 'all') {
                pitch.show()
            } else if (value === 'pbwp' && pitch.data.is_runner_pbwp) {
                pitch.show()
            } else if (value === 'five' && pitch.data.x_runner_pbwp > 0.05) {
                pitch.show()
            } else if (value === 'one' && pitch.data.x_runner_pbwp > 0.01) {
                pitch.show()
            } else {
                pitch.hide()
            }
        })
    }

    getHoveredPitch(raycaster) {
        const intersects = raycaster.intersectObjects(this.pitch_group.children)
        if (intersects.length) {
            const {
                done,
                data: { play_id },
            } = intersects[0].object.userData
            // Don't worry about hovering if the pitches are still moving
            if (!done) {
                return null
            }

            return this.pitch_map[play_id]
        }

        return null
    }

    drawPitches() {
        this.pitches.forEach((pitch) => pitch.draw())
    }

    resetPitches() {
        this.pitches.forEach((pitch) => pitch.reset())
    }

    togglePitches() {
        this.show_pitches = !this.show_pitches

        if (this.show_pitches) {
            this.pitches.forEach((pitch) => pitch.reset())
        } else {
            this.pitches.forEach((pitch) => pitch.hide())
        }
    }

    // filterData() {
    //     this.filtered_data = this.data.slice(0, 8)

    //     this.updateBins()

    //     this.updatePitches()
    // }

    filterPitches({ difficulty, is_pbwp, pitch_type }) {
        // Filter for block/pbwp
        let data = this.data.slice(0)
        if (is_pbwp === true) {
            data = data.filter((d) => d.is_runner_pbwp)
        } else if (is_pbwp === false) {
            data = data.filter((d) => !d.is_runner_pbwp)
        }

        if (!difficulty.length && !pitch_type.length) {
            this.filtered_data = data
        } else if (difficulty.length && !pitch_type.length) {
            // Yikes, let's just use the first value
            const [val] = difficulty
            this.filtered_data = data.filter((d) => d.bin_x_runner_pbwp === val)
        } else if (!difficulty.length && pitch_type.length) {
            this.filtered_data = data.filter((d) =>
                pitch_type.includes(d.api_pitch_type_subgroup)
            )
        } else {
            // Yikes, let's just use the first value
            const [val] = difficulty
            this.filtered_data = data.filter(
                (d) =>
                    d.bin_x_runner_pbwp === val &&
                    pitch_type.includes(d.api_pitch_type_subgroup)
            )
        }

        this.updateBins()

        this.updatePitches()
    }

    filterPitchType(selected_types) {
        if (!selected_types.length) {
            this.filtered_data = this.data
        } else {
            this.filtered_data = this.data.filter((d) =>
                selected_types.includes(d.api_pitch_type_subgroup)
            )
        }
        this.updateBins()

        this.updatePitches()
    }

    updateBins() {
        this.binData()

        // Mutate
        this.xz_bin_objects.forEach((obj, i) => {
            const { bin } = this.xz_bins_flat_unfiltered[i]
            // eslint-disable-next-line no-param-reassign
            obj.userData.bin = bin
        })

        this.xy_bin_objects.forEach((obj, i) => {
            const { bin } = this.xy_bins_flat_unfiltered[i]
            // eslint-disable-next-line no-param-reassign
            obj.userData.bin = bin
        })
    }

    // Just remove and restart
    updatePitches() {
        this.group.remove(this.pitch_group)

        this.createPitchObjects()

        // this.show_pitches = false
    }

    toggleSelected(light_on) {
        this.spot_light.visible = light_on
        // TODO:
        // this.spot_light.visible = false
        this.ground.visible = light_on
        if (this.text) {
            this.text.visible = light_on
        } else {
            setTimeout(() => this.toggleSelected(light_on), 100)
        }
    }

    addLights() {
        this.spot_light = new THREE.SpotLight(
            0xffffff,
            1,
            100,
            Math.PI / 8,
            0.9
        )
        this.spot_light.position.set(0, 12, 9)
        this.spot_light.visible = false

        this.group.add(this.spot_light)

        this.point_light = new THREE.PointLight(0xffffff, 0.5, 100, 2)
        this.point_light.position.x = 0
        this.point_light.position.y = 1
        this.point_light.position.z = 1.5

        this.group.add(this.point_light)
    }
}
